import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth, isCustomRole } from "@/lib/auth.jsx";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import RPMDashboard from "@/pages/rpm/RPMDashboard";
import PMDashboard from "@/pages/pm/PMDashboard";
import EventDetail from "@/pages/pm/EventDetail";
import SMDashboard from "@/pages/sm/SMDashboard";
import CWDashboard from "@/pages/cw/CWDashboard";
import HeadAcademicsDashboard from "@/pages/academics/HeadAcademicsDashboard";
import SEODashboard from "@/pages/seo/SEODashboard";
import PrincipalDashboard from "@/pages/principal/PrincipalDashboard";
import PrincipalTodosPage from "@/pages/principal/PrincipalTodosPage";
import CustomRoleDashboard from "@/pages/custom/CustomRoleDashboard";
import TasksPage from "@/pages/shared/TasksPage";
import MeetingsPage from "@/pages/shared/MeetingsPage";
import SchoolDirectoryPage from "@/pages/shared/SchoolDirectoryPage";
import VendorDirectoryPage from "@/pages/shared/VendorDirectoryPage";
import MemberDirectoryPage from "@/pages/admin/MemberDirectoryPage";
import DesignerDashboard from "@/pages/designer/DesignerDashboard";
import DesignerEventCalendar from "@/pages/designer/DesignerEventCalendar";
import AccountantDashboard from "@/pages/accountant/AccountantDashboard";
import SchoolLedger from "@/pages/accountant/SchoolLedger";
import DesignGalleryPage from "@/pages/shared/DesignGalleryPage";
import ContentCreatorPage from "@/pages/shared/ContentCreatorPage";
import DocumentsPage from "@/pages/shared/DocumentsPage";
import BlogWorkflowPage from "@/pages/shared/BlogWorkflowPage";
import NotesPage from "@/pages/shared/NotesPage";
import ProfilePage from "@/pages/shared/ProfilePage";
import LeaderboardPage from "@/pages/growth/LeaderboardPage";
import RewardsPage from "@/pages/growth/RewardsPage";
import RecognitionsPage from "@/pages/growth/RecognitionsPage";
import HelpDeskPage from "@/pages/growth/HelpDeskPage";
import LearnPage from "@/pages/growth/LearnPage";
import TeamPage from "@/pages/shared/TeamPage";
import ChatPage from "@/pages/shared/ChatPage";
import ReportsPage from "@/pages/admin/ReportsPage";
import SocialPostsTimelinePage from "@/pages/admin/SocialPostsTimelinePage";
import AdminTasksPage from "@/pages/admin/AdminTasksPage";
import SchedulerPage from "@/pages/shared/SchedulerPage";
import ChangePasswordModal from "@/pages/shared/ChangePasswordModal";

const DOC_ROLES = ["admin", "regional_pm", "head_academics", "head_media_comm", "media_comm_executive", "project_manager", "designer"];
const NOTE_ROLES = ["admin", "regional_pm", "head_academics", "social_media_manager", "social_media_executive", "head_media_comm", "media_comm_executive", "project_manager", "seo", "designer"];
const TEAM_ROLES = ["admin", "regional_pm", "social_media_manager", "head_media_comm"];
const BLOG_WORKFLOW_ROLES = ["admin", "head_media_comm", "media_comm_executive"];

export default function DashboardRouter() {
  const { user, customRole } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role;
  const isCustom = isCustomRole(role);
  // For custom roles, the enabled feature flags drive which routes are mounted.
  const customFeatures = new Set(customRole?.features || []);
  const has = (f) => customFeatures.has(f);

  let DashEl;
  if (role === "admin") DashEl = AdminDashboard;
  else if (role === "regional_pm") DashEl = RPMDashboard;
  else if (role === "project_manager") DashEl = PMDashboard;
  else if (role === "social_media_manager" || role === "social_media_executive") DashEl = SMDashboard;
  else if (role === "content_writer" || role === "head_media_comm" || role === "media_comm_executive") DashEl = CWDashboard;
  else if (role === "head_academics") DashEl = HeadAcademicsDashboard;
  else if (role === "seo") DashEl = SEODashboard;
  else if (role === "school_principal") DashEl = PrincipalDashboard;
  else if (role === "designer") DashEl = DesignerDashboard;
  else if (role === "accountant") DashEl = AccountantDashboard;
  else if (isCustom) DashEl = CustomRoleDashboard;
  else DashEl = () => <div className="p-12">Unknown role: {role}</div>;

  return (
    <>
      <ChangePasswordModal />
      <Routes>
        <Route index element={<DashEl />} />
        {/* Always-on routes for every authenticated user */}
        <Route path="meetings" element={<MeetingsPage />} />
        <Route path="scheduler" element={<SchedulerPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="rewards" element={<RewardsPage />} />
        <Route path="recognitions" element={<RecognitionsPage />} />
        <Route path="helpdesk" element={<HelpDeskPage />} />
        <Route path="learn" element={<LearnPage />} />

        {/* Built-in role-based routes */}
        {(!isCustom || has("tasks")) && <Route path="tasks" element={<TasksPage />} />}
        {role === "admin" && <Route path="all-tasks" element={<AdminTasksPage />} />}
        {(!isCustom || has("ai")) && <Route path="ai" element={<ContentCreatorPage />} />}
        {role === "admin" && <Route path="reports" element={<ReportsPage />} />}
        {(role === "admin" || role === "regional_pm" || role === "project_manager" || role === "social_media_manager") && <Route path="social-timeline" element={<SocialPostsTimelinePage />} />}
        {(TEAM_ROLES.includes(role) || (isCustom && has("team"))) && <Route path="team" element={<TeamPage />} />}
        {(DOC_ROLES.includes(role) || (isCustom && has("documents"))) && <Route path="documents" element={<DocumentsPage />} />}
        {(DOC_ROLES.includes(role) || (isCustom && has("blogs"))) && <Route path="blogs" element={<DocumentsPage kind="blog" pageTitle="Blog Writer" pageSubtitle="Write articles, drafts and posts — auto-saved, A4 preview, PDF + DOCX export." />} />}
        {BLOG_WORKFLOW_ROLES.includes(role) && <Route path="blog-workflow" element={<BlogWorkflowPage />} />}
        {(NOTE_ROLES.includes(role) || (isCustom && has("notes"))) && <Route path="notes" element={<NotesPage />} />}
        {((role === "project_manager" || role === "regional_pm" || role === "admin") || (isCustom && has("schools"))) && <Route path="schools" element={<SchoolDirectoryPage />} />}
        {(role === "admin" || role === "regional_pm" || role === "project_manager") && <Route path="vendors" element={<VendorDirectoryPage />} />}
        {role === "admin" && <Route path="members" element={<MemberDirectoryPage />} />}
        {(role === "designer" || role === "admin") && <Route path="design-gallery" element={<DesignGalleryPage />} />}
        {(role === "designer" || role === "admin" || role === "regional_pm" || role === "project_manager" || role === "social_media_manager" || role === "social_media_executive") && <Route path="design-events/:eventId" element={<DesignerEventCalendar />} />}
        {(role === "social_media_manager" || role === "social_media_executive" || (isCustom && has("calendar"))) && <Route path="social-calendar" element={<SMDashboard />} />}
        {role === "school_principal" && <Route path="todos" element={<PrincipalTodosPage />} />}
        {(role === "accountant" || role === "admin") && <Route path="accountant/school/:accountId" element={<SchoolLedger />} />}
        {((role === "project_manager" || role === "admin" || role === "regional_pm") || (isCustom && has("events"))) && <Route path="events/:eventId/*" element={<EventDetail />} />}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  );
}
